import type { Card, Effect } from '../game/types';

type KeywordKey = 'Exile' | 'Discard' | 'Chain' | 'Stun' | 'Addendum' | 'Delay';

type KeywordItem = {
  key: KeywordKey;
  label: string;
  description: string;
};

export type CardTooltipExtraRow = {
  key: string;
  label: string;
  description: string;
};

const KEYWORD_COPY: Record<KeywordKey, KeywordItem> = {
  Exile: {
    key: 'Exile',
    label: 'Exile',
    description: 'Exits the deck after played.',
  },
  Discard: {
    key: 'Discard',
    label: 'Discard',
    description: 'Remove a card from your hand.',
  },
  Chain: {
    key: 'Chain',
    label: 'Chain',
    description: 'Play another card after this one. All black cards have this property.',
  },
  Stun: {
    key: 'Stun',
    label: 'Stun',
    description: 'A stunned fighter still gets a turn and draws, but cannot play or Recast. They can only Focus.',
  },
  Addendum: {
    key: 'Addendum',
    label: 'Addendum',
    description: 'Adds an extra effect to the base card for an extra cost.',
  },
  Delay: {
    key: 'Delay',
    label: 'Delay',
    description: 'Some part of this card resolves later (effect and/or cost).',
  },
};

function hasEffect(card: Card, type: Effect['type']) {
  return (card.effects ?? []).some((effect) => effect.type === type);
}

export function getCardKeywordTooltipItems(card: Card): KeywordItem[] {
  const keywords = new Set((card.keywords ?? []).map((keyword) => String(keyword).trim()));
  const items: KeywordItem[] = [];

  // Global rule: Magic cards implicitly behave as if they have "Chain".
  // The card art/tags may not show the keyword, but the tooltip should.
  const implicitMagicChain = card.kind === 'magic';

  if (keywords.has('Exile')) items.push(KEYWORD_COPY.Exile);
  if (hasEffect(card, 'discard')) items.push(KEYWORD_COPY.Discard);
  if (keywords.has('Chain') || implicitMagicChain) items.push(KEYWORD_COPY.Chain);
  if (hasEffect(card, 'stun')) items.push(KEYWORD_COPY.Stun);
  if (card.addendum) items.push(KEYWORD_COPY.Addendum);
  if ((card.delayTurns ?? 0) > 0 || (card.delayed?.length ?? 0) > 0 || ((card as any).costDelayTurns ?? 0) > 0) items.push(KEYWORD_COPY.Delay);

  return items;
}

export default function CardKeywordTooltip({
  card,
  extraRows = [],
}: {
  card: Card;
  extraRows?: CardTooltipExtraRow[];
}) {
  const items = getCardKeywordTooltipItems(card);
  const rows: CardTooltipExtraRow[] = [
    ...extraRows,
    ...items.map((item) => ({
      key: item.key,
      label: item.label,
      description: item.description,
    })),
  ];

  if (rows.length === 0) return null;

  return (
    <div className="cardKeywordTooltip" role="tooltip" aria-hidden="true">
      {rows.map((row) => (
        <div key={row.key} className="cardKeywordTooltipRow">
          <span className="cardKeywordTooltipLabel">{row.label}:</span>{' '}
          <span className="cardKeywordTooltipText">{row.description}</span>
        </div>
      ))}
    </div>
  );
}
